import type { FlowVersion } from '../../types'

// Intentionally uses native Date/Intl instead of the shared `v2/utils/dayjs`:
// these formatters run inside component tests, and a sibling suite mock.module's
// `v2/utils/dayjs` (a shared module — see the bun mock-leak gotcha in AGENTS.md),
// which would break us in the full test run. Formatting is local-timezone display
// only, so dayjs plugins aren't needed here.

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

// `created_at` is a Unix timestamp in SECONDS (backend sends int(ts.timestamp()),
// matching the rest of the flow API). JS Date expects MILLISECONDS — passing the
// raw seconds yields ~Jan 1970, so convert here.
const toDate = (unixSeconds: number): Date => new Date(unixSeconds * 1000)

export const formatVersionDateGroup = (createdAt: number): string => {
  const date = toDate(createdAt)
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

export const formatVersionTime = (createdAt: number): string =>
  toDate(createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

// Vibe contributions take precedence over the editor name; manual edits made
// before per-version editor tracking shipped have no name and fall back to a
// generic label.
export const getVersionEditorLabel = (version: FlowVersion): string => {
  if (version.is_vibe) return 'Vibe Agent'
  return version.edited_by ?? 'Manual edit'
}

// change_summary is already length-bounded server-side (AI: <=10 words / <=80 chars;
// manual edits: <=100 chars), so don't re-clamp here — a word cap would silently
// truncate a longer manual note, including in the `title` tooltip. Just collapse
// whitespace so a pasted multi-line value stays single-line; the row's line-clamp and
// the banner's truncate handle visual overflow. Returns null when there's nothing to show.
export const formatChangeSummary = (summary: string | null): string | null => {
  const normalized = summary?.replace(/\s+/g, ' ').trim()
  return normalized || null
}

export const sortVersionsDesc = (versions: FlowVersion[]): FlowVersion[] =>
  [...versions].sort((a, b) => b.version - a.version)

// Buckets versions (already newest-first) into contiguous date-labeled groups,
// preserving order so the dropdown can render a header per calendar day.
export const groupVersionsByDate = (
  versions: FlowVersion[],
): Array<{ label: string; versions: FlowVersion[] }> => {
  const groups: Array<{ label: string; versions: FlowVersion[] }> = []
  for (const version of versions) {
    const label = formatVersionDateGroup(version.created_at)
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.versions.push(version)
    else groups.push({ label, versions: [version] })
  }
  return groups
}
